export interface Plan {
  id: number
  public_id?: string
  name: string
  description: string
  stripe_price_id: string
  stripe_product_id?: string
  amount: number | string
  currency: string
  interval: 'month' | 'year'
  is_active: boolean
  features?: string[]
  success_fee_percentage?: number
  success_fee_months?: number
  upfront_months?: number
  is_popular?: boolean
  formatted_amount?: string
  example?: string
  terms?: string[]
  duration_months?: number
  created_at?: string
  updated_at?: string
  has_plan?: boolean
}

export interface Product {
  id: number
  public_id: string
  name: string
  description: string
  stripe_price_id: string
  stripe_product_id?: string
  amount: number | string
  formatted_amount?: string
  currency: string
  duration_months: number
  is_active: boolean
  features?: string[]
  included_platforms?: string[]
  created_at?: string
  updated_at?: string
}

export interface ProductPurchase {
  id: number
  product?: Product
  product_name?: string
  amount: number | string
  status: 'pending' | 'paid'
  starts_at: string | null
  expires_at: string | null
  is_active_access?: boolean
  stripe_checkout_session_id?: string
  stripe_payment_intent_id?: string
  created_at?: string
  updated_at?: string
}

export interface MyOfferResponse {
  has_plan: boolean
  has_product: boolean
  assigned_plan: Plan | null
  assigned_product: Product | null
  active_purchase: ProductPurchase | null
  latest_purchase: ProductPurchase | null
}

export interface AssignedPlanResponse {
  plan_id: number
  plan_public_id?: string
  plan_name: string
  plan_description: string
  stripe_price_id: string
  amount: number
  currency: string
  formatted_amount: string
  interval: 'month' | 'year'
  is_active: boolean
  has_plan: boolean
}

export interface StripeCustomer {
  id: number
  user: number
  stripe_customer_id: string
  email: string
}

export interface PaymentMethod {
  id: number
  stripe_customer: number
  stripe_payment_method_id: string
  type: string
  card_brand: string
  card_last4: string
  card_exp_month: number
  card_exp_year: number
  is_default: boolean
  is_active: boolean
}

export interface Subscription {
  id: number
  user: number
  plan: Plan | number
  plan_name?: string | null
  plan_amount?: string | null
  plan_currency?: string | null
  plan_interval?: 'month' | 'year' | null
  stripe_subscription_id: string
  status: SubscriptionStatus
  tuition_status?: 'finished'
  access_expires_at?: string | null
  paid_job_support_assigned_at?: string | null
  current_period_start: string
  current_period_end: string
  cancel_at_period_end: boolean
  canceled_at?: string | null
  trial_start?: string | null
  trial_end?: string | null
}

export type SubscriptionStatus = 
  | 'active' 
  | 'trialing' 
  | 'past_due' 
  | 'canceled' 
  | 'incomplete' 
  | 'incomplete_expired' 
  | 'unpaid'

export interface SubscriptionPayment {
  id: number
  subscription: number
  subscription_user_name?: string
  subscription_plan_name?: string
  stripe_payment_intent_id: string
  stripe_payment_url?: string | null
  amount: number
  formatted_amount?: string
  currency: string
  status: PaymentStatus
  status_display?: string
  payment_date: string
  succeeded_date?: string | null
  failure_reason?: string | null
  is_active?: boolean
  created_at?: string
  updated_at?: string
}

export type PaymentStatus = 
  | 'pending' 
  | 'paid' 
  | 'failed' 
  | 'canceled'

export interface SetupIntent {
  client_secret: string
}

export interface PaymentIntent {
  client_secret: string
}

export interface CreateSubscriptionRequest {
  plan_id: number
  trial_period_days?: number
}

export interface CancelSubscriptionRequest {
  subscription_id: number
  at_period_end: boolean
}

export interface AttachPaymentMethodRequest {
  payment_method_id: string
  customer_id: string
}

export interface SetDefaultPaymentMethodRequest {
  payment_method_id: string
  customer_id: string
}

export interface CreatePaymentIntentRequest {
  amount: number
  currency?: string
  payment_method_id?: string
}

export interface ConfirmPaymentIntentRequest {
  payment_intent_id: string
}

export interface CreateSetupIntentRequest {
  customer_id: string
}

// Pagination types
export interface PaginationInfo {
  current_page: number
  total_pages: number
  page_size: number
  has_next: boolean
  has_previous: boolean
}

export interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
  page_info: PaginationInfo
}

export interface PaymentFilters {
  page?: number
  page_size?: number
  status?: PaymentStatus
  currency?: string
  search?: string
  ordering?: string
}

export type PaymentOrdering = 
  | 'payment_date'
  | '-payment_date'
  | 'amount'
  | '-amount'
  | 'status'
  | '-status'

// UI State types
export interface SubscriptionUIState {
  isCreatingSubscription: boolean
  isCancelingSubscription: boolean
  isAddingPaymentMethod: boolean
  isLoading: boolean
  error: string | null
}

// Subscription status helpers
export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Past Due',
  canceled: 'Canceled',
  incomplete: 'Incomplete',
  incomplete_expired: 'Expired',
  unpaid: 'Unpaid'
}

export const SUBSCRIPTION_STATUS_COLORS: Record<SubscriptionStatus, string> = {
  active: 'green',
  trialing: 'blue',
  past_due: 'yellow',
  canceled: 'gray',
  incomplete: 'orange',
  incomplete_expired: 'red',
  unpaid: 'red'
}

export type SubscriptionStatusAlertVariant = 'warning' | 'danger' | 'muted'

export interface SubscriptionStatusAlertStyle {
  container: string
  iconBg: string
  icon: string
  title: string
  text: string
}

export interface SubscriptionStatusAlertInfo {
  title: string
  body: string
  highlight?: string
  afterHighlight?: string
  secondaryBody?: string
  styles: SubscriptionStatusAlertStyle
}

export type SubscriptionPrimaryActionEvent = 'restore' | 'subscribe' | 'reactivate' | 'retry-payment'

export interface SubscriptionStatusPrimaryAction {
  label: string
  event: SubscriptionPrimaryActionEvent
}

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  pending: 'Pending',
  paid: 'Paid',
  failed: 'Failed',
  canceled: 'Canceled'
}

export const PAYMENT_STATUS_COLORS: Record<PaymentStatus, string> = {
  pending: 'yellow',
  paid: 'green',
  failed: 'red',
  canceled: 'gray'
}

// Card brand helpers
export const CARD_BRAND_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay'
}

export const CARD_BRAND_COLORS: Record<string, string> = {
  visa: '#1A1F71',
  mastercard: '#EB001B',
  amex: '#006FCF',
  discover: '#FF6000',
  diners: '#0079BE',
  jcb: '#003B8C',
  unionpay: '#E21836'
}
